import React, { useState, useEffect } from 'react';
import './EventAttendanceModal.css';
import API_BASE_URL from '../../config/api';

const STATUS_OPTIONS = [
    { value: 1, label: 'Presente' },
    { value: 2, label: 'Faltou' },
    { value: 3, label: 'Atrasado' },
    { value: 4, label: 'Lesionado' },
    { value: 5, label: 'Justificado' },
];

const EventAttendanceModal = ({ isOpen, onClose, event, teamId }) => {
    const [athletes, setAthletes] = useState([]);
    const [attendance, setAttendance] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen && event && teamId) {
            fetchData();
        }
    }, [isOpen, event, teamId]);

    if (!isOpen || !event || event.eventType !== 2) return null;

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };

            const [teamRes, attendanceRes] = await Promise.all([
                fetch(`${API_BASE_URL}/api/teams/${teamId}`, { headers }),
                fetch(`${API_BASE_URL}/api/attendance/event/${event.id}`, { headers }),
            ]);

            if (!teamRes.ok) throw new Error('Falha ao carregar equipa.');
            const teamData = await teamRes.json();

            let existingAttendance = [];
            if (attendanceRes.ok) existingAttendance = await attendanceRes.json();

            const attendanceMap = {};
            existingAttendance.forEach(r => {
                attendanceMap[r.athleteId] = { status: r.status, reason: r.reason || '' };
            });

            const initialAttendance = {};
            (teamData.athletes || []).forEach(a => {
                initialAttendance[a.id] = attendanceMap[a.id] ?? { status: 1, reason: '' };
            });

            setAthletes(teamData.athletes || []);
            setAttendance(initialAttendance);
        } catch (err) {
            console.error(err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = (athleteId, newStatus) => {
        setAttendance(prev => ({
            ...prev,
            [athleteId]: { ...prev[athleteId], status: parseInt(newStatus, 10) },
        }));
    };

    const handleReasonChange = (athleteId, newReason) => {
        setAttendance(prev => ({
            ...prev,
            [athleteId]: { ...prev[athleteId], reason: newReason },
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const payload = {
                eventId: event.id,
                attendances: Object.keys(attendance).map(athleteId => ({
                    athleteId: parseInt(athleteId, 10),
                    status: attendance[athleteId].status,
                    reason: attendance[athleteId].reason,
                })),
            };

            const response = await fetch(`${API_BASE_URL}/api/attendance/batch`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) throw new Error('Falha ao guardar assiduidade.');
            onClose();
        } catch (err) {
            console.error(err);
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const formatDate = dt =>
        new Date(dt).toLocaleDateString('pt-PT', { day: '2-digit', month: 'long' });

    const formatTime = dt =>
        new Date(dt).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' });

    const getInitials = athlete =>
        athlete.jerseyNumber
            ? `#${athlete.jerseyNumber}`
            : athlete.name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();

    const presentCount = Object.values(attendance).filter(r => r.status === 1).length;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="attendance-modal" onClick={e => e.stopPropagation()}>
                <header className="modal-header">
                    <h2>
                        <i className="fas fa-clipboard-check" />
                        Registar Assiduidade
                    </h2>
                    <button className="close-btn" onClick={onClose} aria-label="Fechar">
                        &times;
                    </button>
                </header>

                <div className="modal-body">
                    <div className="event-summary-bar">
                        <div className="event-info-main">
                            <h3>{event.title}</h3>
                            <div className="event-date-meta">
                                <span className="meta-item"><i className="fas fa-calendar-alt" /> {formatDate(event.startDateTime)}</span>
                                <span className="meta-item"><i className="fas fa-clock" /> {formatTime(event.startDateTime)}</span>
                            </div>
                        </div>
                        <div className="attendance-stats">
                            <span className="stat-pill present">{presentCount} Presentes</span>
                            <span className="stat-pill total">{athletes.length} Total</span>
                        </div>
                    </div>

                    {loading ? (
                        <div className="loading-spinner">A carregar atletas…</div>
                    ) : (
                        <div className="attendance-list">
                            {athletes.map(athlete => {
                                const record = attendance[athlete.id] || { status: 1, reason: '' };
                                return (
                                    <div key={athlete.id} className={`attendance-row status-${record.status}`}>
                                        <div className="col-name">
                                            <div className="athlete-avatar-small">
                                                {getInitials(athlete)}
                                            </div>
                                            <span>{athlete.name}</span>
                                        </div>

                                        <div className="col-status">
                                            <select
                                                value={record.status}
                                                onChange={e => handleStatusChange(athlete.id, e.target.value)}
                                                className={`status-select status-${record.status}`}
                                            >
                                                {STATUS_OPTIONS.map(o => (
                                                    <option key={o.value} value={o.value} style={{ color: '#334155', background: 'white' }}>{o.label}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="col-reason">
                                            {record.status !== 1 && (
                                                <input
                                                    type="text"
                                                    placeholder="Justificação (opcional)…"
                                                    value={record.reason}
                                                    onChange={e => handleReasonChange(athlete.id, e.target.value)}
                                                />
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                <footer className="modal-footer">
                    <button className="btn-secondary" onClick={onClose} disabled={saving}>
                        Cancelar
                    </button>
                    <button className="btn-primary" onClick={handleSave} disabled={saving || loading}>
                        {saving ? 'A guardar…' : 'Guardar Assiduidade'}
                    </button>
                </footer>
            </div>
        </div>
    );
};

export default EventAttendanceModal;